import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QrCode, RefreshCw, Copy, Smartphone, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dealService } from '@/services/deal.service';
import { merchantService } from '@/services/merchant.service';

const SCANNER_URL = import.meta.env.VITE_SCANNER_URL || 'https://scan.run2sale.com';

export function ScannerSetupPage() {
  const [copiedPin, setCopiedPin] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);

  const { data: stores = [], isLoading: storesLoading } = useQuery({
    queryKey: ['my-stores'],
    queryFn: merchantService.getMyStores,
  });

  const store = stores[0]; // Use first store for now

  const generatePinMutation = useMutation({
    mutationFn: dealService.generateScannerPin,
    onSuccess: (data) => {
      toast.success(`New scanner PIN generated: ${data.pin}`);
    },
    onError: () => toast.error('Failed to generate PIN'),
  });

  const copyToClipboard = async (text: string, type: 'pin' | 'url') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'pin') {
        setCopiedPin(true);
        setTimeout(() => setCopiedPin(false), 2000);
      } else {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (storesLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <QrCode className="h-8 w-8" />
          QR Scanner Setup
        </h1>
        <p className="mt-1 text-muted-foreground">
          Set up the QR scanner for your cashiers to validate customer claims
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Scanner PIN */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm text-white">
                1
              </span>
              Scanner PIN
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Your cashiers will use this PIN to log into the scanner web app. Generate a new PIN
              if you need to revoke access.
            </p>

            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-lg border bg-gray-50 p-4 text-center">
                <p className="text-sm text-muted-foreground">Current Scanner PIN</p>
                <p className="mt-1 text-3xl font-mono font-bold tracking-wider">
                  {generatePinMutation.data?.pin || store?.scannerPinPlain || '------'}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    copyToClipboard(
                      generatePinMutation.data?.pin || store?.scannerPinPlain || '',
                      'pin'
                    )
                  }
                  disabled={!generatePinMutation.data?.pin && !store?.scannerPinPlain}
                >
                  {copiedPin ? (
                    <CheckCircle className="mr-1 h-4 w-4" />
                  ) : (
                    <Copy className="mr-1 h-4 w-4" />
                  )}
                  {copiedPin ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generatePinMutation.mutate()}
                  disabled={generatePinMutation.isPending}
                >
                  <RefreshCw className="mr-1 h-4 w-4" />
                  {generatePinMutation.isPending ? 'Generating...' : 'New PIN'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Scanner URL */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm text-white">
                2
              </span>
              Open Scanner on Cashier Phone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Open the scanner web app on any phone. No app download required -- it works in any
              modern browser.
            </p>

            <div className="flex items-center gap-3 rounded-lg border bg-gray-50 p-4">
              <Smartphone className="h-5 w-5 text-muted-foreground" />
              <code className="flex-1 text-sm font-medium">{SCANNER_URL}</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(SCANNER_URL, 'url')}
              >
                {copiedUrl ? (
                  <CheckCircle className="mr-1 h-4 w-4" />
                ) : (
                  <Copy className="mr-1 h-4 w-4" />
                )}
                {copiedUrl ? 'Copied' : 'Copy'}
              </Button>
            </div>

            {/* QR code for easy access */}
            <div className="flex flex-col items-center rounded-lg border p-6">
              <p className="mb-4 text-sm font-medium">
                Or scan this QR code with the cashier's phone:
              </p>
              <div className="flex h-48 w-48 items-center justify-center rounded-lg border-2 border-dashed bg-white">
                <div className="text-center">
                  <QrCode className="mx-auto h-16 w-16 text-gray-300" />
                  <p className="mt-2 text-xs text-muted-foreground">
                    QR code for {SCANNER_URL}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-sm text-white">
                3
              </span>
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                  1
                </span>
                <span>
                  Cashier opens <strong>{SCANNER_URL}</strong> on their phone and enters the
                  store PIN to log in.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                  2
                </span>
                <span>
                  When a customer arrives with a Run2Sale deal, they show their QR code on their
                  phone.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                  3
                </span>
                <span>
                  Cashier taps "Scan" and points their camera at the customer's QR code.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                  4
                </span>
                <span>
                  The scanner shows the discount amount. Cashier taps "Confirm Sale" to apply
                  the discount, or "Item Unavailable" if out of stock.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium">
                  5
                </span>
                <span>
                  The customer receives a confirmation and the deal is marked as claimed in your
                  dashboard.
                </span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
